import React, { useState, useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useToastContext from "hooks/useToastContext";
import AuthContext from "context/AuthContext";
import { apiPost } from "utils/apiFetch";
import apiPath from "utils/pathObj";
import ErrorMessage from "components/ErrorMessage";
import OInputField from "components/reusable/OInputField";
import formValidation from "utils/formValidation";
import LiveFormPreview from "./Preview";
import { useNavigate } from "react-router-dom";

const FIELD_TYPES = [
  "text",
  "textarea",
  "heading",
  "textEditor",
  "input",
  "radio",
  "checkbox",
  "date",
  "file",
  "select",
];

const AddOnboardingForm = () => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setError,
    reset,
    watch,
  } = useForm({ mode: "onChange" });

  const [category, setCategory] = useState("");
  const [customFields, setCustomFields] = useState([]);
  const [dynamicFields, setDynamicFields] = useState([
    {
      fieldType: "",
      fieldLabel: "",
      fieldOptions: "",
      mandatory: false,
      boldText: false,
    },
  ]);
  const [fieldValues, setFieldValues] = useState({});
  const [optionsDirection, setOptionsDirection] = useState("");
  const [layouts, setLayouts] = useState({});

  const watchFields = watch();
  const formName = watch("formName");

  const handleDynamicFieldChange = (index, key, value) => {
    setDynamicFields((prev) => {
      const newFields = [...prev];
      newFields[index][key] = value;
      return newFields;
    });
  };

  const addDynamicFieldBlock = () => {
    setDynamicFields((prev) => [
      ...prev,
      {
        fieldType: "",
        fieldLabel: "",
        fieldOptions: "",
        mandatory: false,
        boldText: false,
      },
    ]);
  };

  const handleAddField = () => {
    const newFields = dynamicFields
      .map((field) => {
        const { fieldType, fieldLabel, fieldOptions, mandatory, boldText } =
          field;

        // Skip validation for heading and textEditor fields
        if (!["heading", "textEditor"].includes(fieldType)) {
          if (!fieldType || !fieldLabel.trim()) {
            notification.error(
              "Please fill all field blocks with type and label"
            );
            return null;
          }
        }

        // Determine styles based on field type and options
        let styles = {
          container: "w-full",
          label: `text-sm font-medium ${mandatory ? "text-red-500" : ""}`,
          input: "border border-gray-300 rounded-md shadow-sm p-2 w-full",
        };

        if (fieldType === "heading") {
          styles = {
            ...styles,
            heading: `text-xl ${boldText ? "font-bold" : "font-normal"} mb-2`,
          };
        } else if (fieldType === "textarea") {
          styles = {
            ...styles,
            textarea:
              "border border-gray-300 rounded-md shadow-sm p-2 w-full h-24",
          };
        } else if (["radio", "checkbox"].includes(fieldType)) {
          styles = {
            ...styles,
            optionsContainer: `flex ${optionsDirection || "flex-row"} gap-4`,
          };
        }

        return {
          id: Date.now() + Math.random(),
          type: fieldType,
          label: fieldLabel.trim(),
          mandatory: mandatory || false,
          boldText: boldText || false,
          options: ["radio", "select", "checkbox"].includes(fieldType)
            ? fieldOptions
                .split(",")
                .map((opt) => opt.trim())
                .filter((opt) => opt.length > 0)
            : [],
          styles,
          value: fieldType === "checkbox" ? [] : "",
        };
      })
      .filter(Boolean);

    if (newFields.length !== dynamicFields.length) return;

    setCustomFields((prev) => [...prev, newFields]);
    setDynamicFields([
      {
        fieldType: "",
        fieldLabel: "",
        fieldOptions: "",
        mandatory: false,
        boldText: false,
      },
    ]);
  };

  const handleDeleteField = (index) => {
    if (dynamicFields.length > 1) {
      setDynamicFields((prev) => prev.filter((_, i) => i !== index));
    } else {
      notification.error("You need to keep at least one field");
    }
  };

  const handleRemoveField = (id) => {
    setCustomFields((prev) => {
      const updatedFields = prev
        .map((group) => group.filter((field) => field.id !== id))
        .filter((group) => group.length > 0);
      return updatedFields;
    });

    setFieldValues((prev) => {
      const newValues = { ...prev };
      delete newValues[id];
      return newValues;
    });

    setLayouts((prev) => {
      const newLayouts = {};
      customFields.forEach((group, groupIndex) => {
        newLayouts[groupIndex] = group
          .filter((field) => field.id !== id)
          .map((field, fieldIndex) => ({
            i: `${groupIndex}-${field.id}`,
            x: field.layout?.x || (fieldIndex % 3) * 4,
            y: field.layout?.y || Math.floor(fieldIndex / 3),
            w: field.layout?.w || (field.type === "textEditor" ? 10 : 4),
            h: field.layout?.h || (field.type === "textEditor" ? 4 : 2),
          }));
      });
      return newLayouts;
    });
  };

  const handleCategoryChange = (e) => {
    const val = e.target.value;
    if (val) {
      setCategory(val);
      clearErrors("category");
    } else {
      setCategory("");
      setError("category", {
        type: "manual",
        message: "Please select a category.",
      });
    }
  };

  const handleBack = () => {
    navigate("/forms-manager");
  };

  const onSubmit = async (data) => {
    if (!category) {
      setError("category", {
        type: "manual",
        message: "Please select a category.",
      });
      return;
    }

    const formData = new FormData();
    const clonedCustomFields = customFields.map((row) =>
      row.map((field) => {
        const fieldWithLayout = {
          ...field,
          layout: field.layout ? { ...field.layout } : undefined,
        };

        // if (field.type === 'file' && field.value instanceof File) {
        //   return {
        //     ...fieldWithLayout,
        //     uploadKey: `file_${field.id}`
        //   }
        // }
        return fieldWithLayout;
      })
    );

    // let fileCount = 0
    // clonedCustomFields.forEach(row => {
    //   row.forEach(field => {
    //     if (field.type === 'file' && field.value instanceof File) {
    //       formData.append(field.uploadKey, field.value)
    //       fileCount++
    //     }
    //   })
    // })

    // const fieldsForJson = clonedCustomFields.map(row =>
    //   row.map(field => {
    //     const cleanField = { ...field }
    //     if (field.type === 'file') {
    //       delete cleanField.value
    //     }
    //     return cleanField
    //   })
    // )
    const fieldsForJson = clonedCustomFields.map((row) =>
      row.map((field) => {
        // Create a clean field object
        const cleanField = { ...field };

        // Only keep the value if the type is 'textEditor'
        if (field.type !== "textEditor") {
          delete cleanField.value;
        }

        // For file fields, always remove the value (handled separately in formData)
        // if (field.type === 'file') {
        //   delete cleanField.value
        // }

        return cleanField;
      })
    );

    formData.append("formName", data.formName);
    formData.append("formType", data.formType);
    formData.append("category", category);
    formData.append("createdBy", user?.companyID);
    formData.append("status", "active");
    formData.append("customFields", JSON.stringify(fieldsForJson));

    try {
      const res = await apiPost(apiPath.addForm, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res?.data?.success) {
        notification.success(res?.data?.message);
        setTimeout(() => {
          handleBack();
        }, 300);
      } else {
        notification.error(res?.data?.message || "Submission failed");
      }
    } catch (err) {
      notification.error(
        err.response?.data?.message || "An error occurred while saving the form"
      );
    }
  };

  return (
    <div className="bg-[#F9F9F9]">
      <div className="py-4 px-3">
        <div className="bg-white border border-[#E9EDF9] rounded-lg">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6 border-b pb-4">
              <h1 className="text-2xl font-bold">{t("ADD FORM")}</h1>
              <button
                onClick={handleBack}
                className="text-gray-500 hover:text-gray-700"
              >
                <span className="text-2xl">×</span>
              </button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <OInputField
                    type="text"
                    name="formName"
                    id="formName"
                    inputLabel={
                      <>
                        {t("Title")}
                        <span className="text-red-500">*</span>
                      </>
                    }
                    register={register("formName", formValidation.name)}
                    errors={errors}
                  />
                </div>
                <div>
                  <OInputField
                    type="text"
                    name="formType"
                    id="formType"
                    inputLabel={
                      <>
                        {t("TYPE")}
                        <span className="text-red-500">*</span>
                      </>
                    }
                    register={register("formType", formValidation.name)}
                    errors={errors}
                  />
                </div>
                <div>
                  <div className="relative w-full z-0 group">
                    <select
                      id="category"
                      className="block py-4 px-3 w-full text-sm border-2 rounded-lg border-[#DFDFDF]"
                      value={category}
                      onChange={handleCategoryChange}
                    >
                      <option value="">{t("SELECT_CATEGORY")}</option>
                      <option key="onboarding" value="onboarding">{t("ONBOARDING")}</option>
                    </select>
                    <label className="absolute text-sm text-[#A5A5A5] transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10">
                      {t("CATEGORY")}
                      <span className="text-red-500">*</span>
                    </label>
                    <ErrorMessage message={errors?.category?.message} />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h3 className="font-medium mb-4">{t("Add Custom Fields")}</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 custum-grid">
                  <div className="space-y-4">
                    {dynamicFields.map((field, index) => (
                      <div
                        key={index}
                        className="border p-4 rounded bg-white selected-field"
                      >
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center gap-2">
                            <select
                              value={field.fieldType}
                              onChange={(e) =>
                                handleDynamicFieldChange(
                                  index,
                                  "fieldType",
                                  e.target.value
                                )
                              }
                              className="border px-3 py-2 rounded-lg border-[#DFDFDF] text-sm flex-1"
                            >
                              <option value="">Select Type</option>
                              {FIELD_TYPES.map((type) => (
                                <option key={type} value={type}>
                                  {type.charAt(0).toUpperCase() + type.slice(1)}
                                </option>
                              ))}
                            </select>

                            {index !== 0 && (
                              <button
                                type="button"
                                onClick={() => handleDeleteField(index)}
                                className="text-white bg-red-500 hover:bg-red-600 rounded-full w-6 h-6 flex items-center justify-center delete-filed"
                                title="Delete field"
                              >
                                ×
                              </button>
                            )}
                          </div>

                          {!["textEditor"].includes(field.fieldType) && (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                placeholder={
                                  field.fieldType === "heading"
                                    ? "Heading Text"
                                    : "Field Label"
                                }
                                value={field.fieldLabel}
                                onChange={(e) =>
                                  handleDynamicFieldChange(
                                    index,
                                    "fieldLabel",
                                    e.target.value
                                  )
                                }
                                className="border px-3 py-2 rounded flex-1 text-md"
                              />
                            </div>
                          )}

                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1">
                              <input
                                type="checkbox"
                                id={`mandatory-${index}`}
                                checked={field.mandatory}
                                onChange={(e) =>
                                  handleDynamicFieldChange(
                                    index,
                                    "mandatory",
                                    e.target.checked
                                  )
                                }
                                className="h-4 w-4"
                              />
                              <label
                                htmlFor={`mandatory-${index}`}
                                className="text-sm"
                              >
                                Required
                              </label>
                            </div>

                            {["heading", "text"].includes(field.fieldType) && (
                              <div className="flex items-center gap-1">
                                <input
                                  type="checkbox"
                                  id={`bold-${index}`}
                                  checked={field.boldText}
                                  onChange={(e) =>
                                    handleDynamicFieldChange(
                                      index,
                                      "boldText",
                                      e.target.checked
                                    )
                                  }
                                  className="h-4 w-4"
                                />
                                <label
                                  htmlFor={`bold-${index}`}
                                  className="text-sm"
                                >
                                  Bold
                                </label>
                              </div>
                            )}
                          </div>

                          {["radio", "select", "checkbox"].includes(
                            field.fieldType
                          ) && (
                            <div className="flex flex-col gap-1">
                              <label className="font-medium text-sm">
                                Options (comma separated)
                              </label>
                              <input
                                type="text"
                                placeholder="e.g. Option1, Option2"
                                value={field.fieldOptions}
                                onChange={(e) =>
                                  handleDynamicFieldChange(
                                    index,
                                    "fieldOptions",
                                    e.target.value
                                  )
                                }
                                className="border px-3 py-2 rounded w-full"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={addDynamicFieldBlock}
                      className="bg-green-500 text-white px-4 py-2 rounded"
                    >
                      Add Another Field
                    </button>
                  </div>

                  <div className="">
                    <div className="grid gap-4">
                      {dynamicFields.some((field) =>
                        ["radio", "checkbox"].includes(field.fieldType)
                      ) && (
                        <div>
                          <label className="block mb-1 text-sm font-medium">
                            Field Direction
                          </label>
                          <select
                            value={optionsDirection}
                            onChange={(e) =>
                              setOptionsDirection(e.target.value)
                            }
                            className="border p-2 w-full rounded"
                          >
                            <option value="">Set Direction</option>
                            <option key="flex-row" value="flex-row">Row</option>
                            <option key="flex-col" value="flex-col">Column</option>
                          </select>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={handleAddField}
                        className="col-span-full bg-LightBlue hover:bg-LightBlue text-white px-4 py-1.5 rounded text-sm"
                      >
                        Add Field to Form
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 pb-4 border-b">
                <h3 className="font-medium text-center">Form Preview</h3>
              </div>

              <div className="bg-gray-50 rounded-lg">
                <div className="flex justify-center">
                  <div className="w-full">
                    <LiveFormPreview
                      customFields={customFields}
                      setCustomFields={setCustomFields}
                      fieldValues={fieldValues}
                      setFieldValues={setFieldValues}
                      layouts={layouts}
                      setLayouts={setLayouts}
                      handleRemoveField={handleRemoveField}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t">
                <button
                  className="bg-gray-500 text-white px-6 py-2 rounded"
                  type="button"
                  onClick={handleBack}
                >
                  Back
                </button>
                <button
                  className="bg-LightBlue text-white px-6 py-2 rounded"
                  type="submit"
                >
                  Save Form
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddOnboardingForm;
